<!-- Loading -->
<div *ngIf="loading" class="flex justify-center items-center py-16">
  <app-mini-loader size="10"></app-mini-loader>
</div>

<app-no-results
  *ngIf="!loading && !showGraph"
  title="This author has no registered coauthors"
></app-no-results>

<div *ngIf="showGraph" class="flex-1 w-full flex flex-col">
  <div
    class="flex flex-col justify-between w-full bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
  >
    <div
      class="flex flex-row flex-wrap justify-between items-center w-full p-4 bg-white border-b border-gray-200 gap-3"
    >
      <div class="flex flex-wrap items-center gap-4 text-gray-700 sm:gap-6">
        <div class="flex items-center gap-2">
          <span class="font-bold text-gray-900">Nodes/Authors:</span>
          <span class="bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold">{{
            d3Nodes.length
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-bold text-gray-900">Relations:</span>
          <span class="bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold">{{
            d3Links.length
          }}</span>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2" *ngIf="d3Nodes.length > 0">
        <button
          type="button"
          class="border border-gray-300 hover:bg-gray-50 py-2 px-3 rounded-lg shadow-sm flex items-center gap-2 transition-colors font-medium text-sm text-gray-700"
          (click)="toggleLegend()"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"></path>
          </svg>
          {{ showLegend ? 'Hide legend' : 'Show legend' }}
        </button>
        <button
          type="button"
          class="bg-red-700 hover:bg-red-800 disabled:bg-red-300 text-white py-2 px-4 rounded-lg shadow-sm flex items-center gap-2 transition-colors font-medium text-sm"
          (click)="onDownloadGraph()"
          [disabled]="selectingRegion || expanding"
        >
          <fa-icon [icon]="faDownload"></fa-icon>
          Download all
        </button>
        <button
          type="button"
          class="border border-red-700 py-2 px-4 rounded-lg shadow-sm flex items-center gap-2 transition-colors font-medium text-sm"
          [ngClass]="selectingRegion ? 'bg-red-700 text-white' : 'text-red-700 hover:bg-red-50'"
          (click)="toggleRegionSelect()"
          [disabled]="expanding"
        >
          <fa-icon [icon]="faVectorSquare"></fa-icon>
          {{ selectingRegion ? 'Cancel' : 'Select region' }}
        </button>
      </div>
    </div>
  </div>

  <div class="w-full max-w-full overflow-hidden mt-4 mat-elevation-z8">
    <div
      class="w-full h-[600px] md:h-[800px] overflow-hidden bg-white rounded relative"
      #downloadEl
    >
      <div
        *ngIf="expanding"
        class="absolute inset-0 z-50 bg-white/75 backdrop-blur-sm flex flex-col items-center justify-center gap-3 cursor-wait select-none"
      >
        <div
          class="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin shadow-md"
        ></div>
        <span
          class="text-sm font-bold text-gray-800 tracking-wide bg-white px-4 py-1.5 rounded-full shadow border border-gray-200"
        >
          Expanding coauthor network, please wait...
        </span>
      </div>

      <div
        *ngIf="notificationMessage"
        class="absolute top-4 left-4 z-40 px-3.5 py-2 rounded-md shadow-sm border bg-white/95 backdrop-blur flex items-center gap-2.5 max-w-sm"
        [ngClass]="{
          'border-l-4 border-l-slate-700 border-gray-200 text-gray-700':
            notificationType === 'success',
          'border-l-4 border-l-gray-400 border-gray-200 text-gray-600':
            notificationType === 'warning',
          'border-l-4 border-l-blue-600 border-gray-200 text-gray-700': notificationType === 'info',
          'border-l-4 border-l-red-600 border-gray-200 text-gray-800': notificationType === 'error',
        }"
      >
        <span class="font-normal text-xs leading-snug">{{ notificationMessage }}</span>
        <button
          type="button"
          (click)="notificationMessage = null"
          aria-label="Dismiss notification"
          class="ml-1 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 text-base leading-none"
        >
          &times;
        </button>
      </div>

      <div
        class="absolute top-4 z-[45] flex flex-col gap-2 transition-all duration-300"
        [ngClass]="selectedNode ? 'right-[345px] md:right-[415px]' : 'right-4'"
      >
        <button
          type="button"
          (click)="zoomIn()"
          title="Zoom in"
          aria-label="Zoom in"
          class="bg-white border shadow-md w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 text-2xl font-bold text-gray-700 select-none"
        >
          +
        </button>
        <button
          type="button"
          (click)="zoomOut()"
          title="Zoom out"
          aria-label="Zoom out"
          class="bg-white border shadow-md w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 text-2xl font-bold text-gray-700 select-none"
        >
          −
        </button>
        <button
          type="button"
          (click)="resetZoom()"
          title="Fit to view"
          aria-label="Fit to view"
          class="bg-white border shadow-md w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-700 mt-2"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9"></circle>
            <path d="M12 8v8M8 12h8"></path>
          </svg>
        </button>
        <button
          type="button"
          (click)="toggleLegend()"
          [title]="showLegend ? 'Hide graph legend' : 'Show graph legend'"
          [attr.aria-label]="showLegend ? 'Hide graph legend' : 'Show graph legend'"
          [ngClass]="showLegend ? 'text-blue-600 border-blue-200 bg-blue-50/50' : 'text-gray-600'"
          class="bg-white border shadow-md w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 mt-2 transition-colors select-none"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"></path>
          </svg>
        </button>
      </div>

      <div
        *ngIf="selectingRegion"
        class="absolute inset-0 z-20 cursor-crosshair select-none"
        (mousedown)="onRegionMouseDown($event)"
        (mousemove)="onRegionMouseMove($event)"
        (mouseup)="onRegionMouseUp()"
        (mouseleave)="onRegionMouseUp()"
      >
        <div
          *ngIf="!regionRect"
          class="absolute top-3 left-1/2 -translate-x-1/2 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap"
          style="background: rgba(0, 0, 0, 0.7)"
        >
          Drag to select the region to download
        </div>
        <div
          *ngIf="regionRect"
          class="absolute border-2 border-red-600 pointer-events-none"
          [style.left.px]="regionRect.x"
          [style.top.px]="regionRect.y"
          [style.width.px]="regionRect.w"
          [style.height.px]="regionRect.h"
          [style.background]="'rgba(220,38,38,0.12)'"
        ></div>
      </div>

      <aside
        *ngIf="selectedNode"
        class="absolute top-0 right-0 h-full w-80 md:w-96 bg-white shadow-xl border-l border-gray-200 z-40 flex flex-col overflow-y-auto"
        aria-label="Author Inspector"
      >
        <div
          class="flex items-center justify-between p-4 bg-gray-900 text-white border-b border-gray-800 sticky top-0 z-10"
        >
          <span class="font-bold text-base tracking-wide">Author Inspector</span>
          <button
            type="button"
            (click)="closePanel()"
            aria-label="Close Author Inspector"
            class="text-gray-400 hover:text-white text-xl font-bold px-2 py-1 leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            &times;
          </button>
        </div>

        <div class="p-5 border-b border-gray-200 bg-gray-50 flex flex-col gap-3">
          <div>
            <h3 class="text-xl font-extrabold text-gray-900 leading-tight">
              {{ selectedNode.author.first_name }} {{ selectedNode.author.last_name }}
            </h3>
            <p class="text-xs text-gray-500 mt-1 font-mono">
              Scopus ID: {{ selectedNode.author.scopus_id }}
            </p>
          </div>
          <div class="flex items-center gap-2 text-sm text-gray-700">
            <span class="font-semibold">Network Connections:</span>
            <span class="bg-blue-100 text-blue-800 font-bold px-2.5 py-0.5 rounded-full text-xs">{{
              selectedNode.degree
            }}</span>
          </div>
          <div class="flex flex-col gap-2 mt-2">
            <a
              [routerLink]="['/profile', selectedNode.author.scopus_id]"
              class="w-full bg-red-700 hover:bg-red-800 text-white text-center py-2 px-4 rounded-lg font-semibold text-sm shadow transition-colors"
            >
              View full profile
            </a>
            <button
              type="button"
              (click)="onPanelExpandNetwork()"
              [disabled]="isNodeExpanded(selectedNode.author.scopus_id) || expanding"
              [ngClass]="
                isNodeExpanded(selectedNode.author.scopus_id) || expanding
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-100 text-gray-800 border-gray-300'
              "
              class="w-full border py-2 px-4 rounded-lg font-semibold text-sm shadow-sm transition-colors"
            >
              {{
                isNodeExpanded(selectedNode.author.scopus_id)
                  ? 'Network already expanded'
                  : expanding
                    ? 'Expanding...'
                    : 'Expand network'
              }}
            </button>
          </div>
        </div>

        <div class="p-5 flex-1 flex flex-col">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-xs font-bold uppercase tracking-wider text-gray-500">
              Top Collaborators
            </h4>
            <span class="text-xs font-semibold text-gray-400"
              >{{ selectedNode.collaborators.length }} total</span
            >
          </div>
          <p
            *ngIf="selectedNode.collaborators.length === 0"
            class="text-sm text-gray-500 italic py-4 text-center"
          >
            No active collaborations in this view.
          </p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              *ngFor="
                let collaborator of showAllCollaborators
                  ? selectedNode.collaborators
                  : selectedNode.collaborators.slice(0, 3);
                let i = index
              "
              (click)="selectNode(collaborator.id)"
              class="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-red-500 hover:bg-red-50/30 cursor-pointer transition-all group text-left"
            >
              <span class="flex items-center gap-2 overflow-hidden">
                <span class="text-xs font-bold text-gray-400 w-4">{{ i + 1 }}.</span>
                <span
                  class="text-sm font-medium text-gray-800 group-hover:text-red-700 truncate"
                  [title]="collaborator.name"
                >
                  {{ collaborator.name }}
                </span>
              </span>
              <span
                class="bg-gray-100 group-hover:bg-red-100 text-gray-800 group-hover:text-red-800 text-xs font-extrabold px-2 py-1 rounded border border-gray-200 group-hover:border-red-200 shrink-0"
              >
                Strength: {{ collaborator.strength }}
              </span>
            </button>
          </div>
          <div *ngIf="selectedNode.collaborators.length > 3" class="mt-4 text-center">
            <button
              type="button"
              (click)="toggleShowAllCollaborators()"
              class="text-red-700 hover:text-red-800 hover:underline text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
            >
              {{
                showAllCollaborators
                  ? 'Show top 3 only'
                  : 'View all (' + selectedNode.collaborators.length + ' collaborators)'
              }}
            </button>
          </div>
        </div>
      </aside>

      <div
        *ngIf="showLegend"
        class="absolute bottom-3 z-[45] bg-white/95 backdrop-blur border border-gray-200 shadow-md rounded-lg px-4 py-2 flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-gray-700 select-none mx-auto w-fit"
        [ngClass]="
          selectedNode
            ? 'left-4 right-[345px] md:right-[415px] max-w-[calc(100%-360px)] md:max-w-[calc(100%-430px)]'
            : 'left-4 right-4 max-w-[calc(100%-2rem)]'
        "
      >
        <div class="flex items-center gap-1.5">
          <span class="w-3 h-3 rounded-full bg-[#111827] border border-gray-300"></span
          ><span>Central Author</span>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="w-3 h-3 rounded-full bg-[#ea580c]"></span
          ><span>Direct Coauthors (Hop 1)</span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="flex -space-x-1">
            <span class="h-3 w-3 rounded-full ring-1 ring-white bg-[#7c3aed]"></span>
            <span class="h-3 w-3 rounded-full ring-1 ring-white bg-[#2563eb]"></span>
            <span class="h-3 w-3 rounded-full ring-1 ring-white bg-[#059669]"></span>
            <span class="h-3 w-3 rounded-full ring-1 ring-white bg-[#0891b2]"></span>
          </div>
          <span>Extended Network (Hops 2–7+)</span>
        </div>
        <div class="w-px h-4 bg-gray-300 hidden sm:block"></div>
        <div class="flex items-center gap-1.5">
          <span class="w-3 h-3 rounded-full bg-[#ca8a04]"></span><span>Expanded (+Coauthors)</span>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="w-3 h-3 rounded-full bg-[#991b1b]"></span><span>Expanded (0 Coauthors)</span>
        </div>
        <button
          type="button"
          (click)="toggleLegend()"
          title="Hide legend"
          aria-label="Hide legend"
          class="ml-1 text-gray-400 hover:text-gray-700 font-bold text-base px-1 leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        >
          &times;
        </button>
      </div>

      <graph #graphRef [nodes]="d3Nodes" [links]="d3Links" [forces]="forces"></graph>
    </div>
  </div>
</div>
